import React, { useCallback } from 'react';
import { Box, IconButton, Tooltip, Typography, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Cancel, ClearAll } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { clearWorkspaceState, closeTab, setActiveTabByPath } from '../../tabs/tabsSlice';

const TabItem = React.memo(({ tab, isActive, onClick, onClose, theme }) => {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        px: 1.4,
        py: 0.6,
        borderRadius: '6px',
        backgroundColor: isActive 
          ? theme.palette.primary.main 
          : 'transparent',
        color: isActive 
          ? '#fff' 
          : theme.palette.text.primary,
        fontWeight: isActive ? 500 : 400,
        fontSize: '0.85rem',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        whiteSpace: 'nowrap',
        position: 'relative',
        '&:hover': {
          backgroundColor: isActive 
            ? theme.palette.primary.main 
            : theme.palette.action.hover,
        },
        // Efecto sutil para tabs inactivos
        '&:not(.active)': {
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '20%',
            height: '2px',
            backgroundColor: theme.palette.primary.light,
            opacity: 0,
            transition: 'opacity 0.2s ease, width 0.2s ease',
            borderRadius: '1px',
          },
          '&:hover::after': {
            opacity: 0.6,
            width: '60%',
          },
        },
      }}
    >
      <Typography
        variant="body2"
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: 'auto',
          opacity: isActive ? 1 : 0.85,
        }}
      >
        {tab.label}
      </Typography>

      <IconButton
        onClick={onClose}
        size="small"
        sx={{
          ml: 0.75,
          p: '2px',
          color: isActive ? '#fff' : theme.palette.text.secondary,
          opacity: 0.7,
          '&:hover': {
            color: isActive ? '#fff' : theme.palette.error.main,
            opacity: 1,
            backgroundColor: 'transparent',
          },
        }}
      >
        <Cancel sx={{ fontSize: 15 }} />
      </IconButton>
    </Box>
  );
});

const CustomTabs = () => {
  const tabs = useSelector((state) => state.tabs.tabs);
  const activeTab = useSelector((state) => state.tabs.activeTab);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();

  const handleTabClick = useCallback((tab) => {
    dispatch(setActiveTabByPath(tab.path));
    navigate(tab.path);
  }, [dispatch, navigate]);

  const handleCloseTab = useCallback((tab, e) => {
    e.stopPropagation();
    if (tab.path === '/invoice/create-new') {
      try {
        localStorage.removeItem('invoice:draft');
      } catch {}
    }
    dispatch(closeTab(tab));
    if (activeTab?.path === tab.path) {
      const nextTab = tabs.find((t) => t.path !== tab.path);
      navigate(nextTab ? nextTab.path : '/');
    }
  }, [dispatch, navigate, activeTab?.path, tabs]);

  const handleCloseAllTabs = useCallback(() => {
    try {
      localStorage.removeItem('invoice:draft');
    } catch {}
    dispatch(clearWorkspaceState());
    navigate('/home');
  }, [dispatch, navigate]);

  return (
    <Box sx={{ 
      px: 0, 
      py: 0.5,
      width: '100%',
      // Contenedor sin scroll visible
      overflow: 'hidden',
    }}>
      <Box 
        display="flex" 
        gap={0.5}
        alignItems="center"
        sx={{
          width: '100%',
          overflowX: 'auto',
          overflowY: 'hidden',
          // Scrollbar completamente invisible pero funcional
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          // Pequeño padding para evitar que los elementos toquen los bordes
          px: 0.5,
        }}
      >
        <AnimatePresence initial={false}>
          {tabs.map((tab, index) => (
            <motion.div
              key={tab.path}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              transition={{ 
                duration: 0.15,
                delay: index * 0.02 
              }}
              style={{ 
                display: 'flex', 
                flexShrink: 0,
                // Evita que las animaciones causen scroll horizontal
                willChange: 'transform'
              }}
            >
              <TabItem
                tab={tab}
                isActive={activeTab?.path === tab.path}
                onClick={() => handleTabClick(tab)}
                onClose={(e) => handleCloseTab(tab, e)}
                theme={theme}
              />
            </motion.div>
          ))}
        </AnimatePresence>
        {tabs.length > 0 && (
          <Tooltip title="Cerrar todas las ventanas">
            <IconButton
              size="small"
              onClick={handleCloseAllTabs}
              sx={{
                ml: 0.5,
                color: theme.palette.text.secondary,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1.5,
                '&:hover': {
                  color: theme.palette.error.main,
                  borderColor: theme.palette.error.light,
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <ClearAll sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export default React.memo(CustomTabs);
